import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText,
  Send,
  Trash2,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ChevronDown,
  Eye,
  Download,
  X
} from 'lucide-react';
import apiClient from '../lib/apiClient';

// ---------------------------------------------------------------------------
// Status enum
// ---------------------------------------------------------------------------
const STATUS = {
  IDLE: 'idle',
  GENERATING: 'generating',
  READY: 'ready',
  APPROVED: 'approved',
  SENDING: 'sending',
  SENT: 'sent',
  DISCARDED: 'discarded'
};

export default function MonthlyReports() {
  // ---- Form state ----
  const [tenants, setTenants] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // ---- Report state ----
  const [status, setStatus] = useState(STATUS.IDLE);
  const [reportData, setReportData] = useState(null);
  const [pdfBase64, setPdfBase64] = useState(null);
  const [pdfFilename, setPdfFilename] = useState('');
  const [error, setError] = useState('');
  const [overrideEmail, setOverrideEmail] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [sendDropdownOpen, setSendDropdownOpen] = useState(false);

  // ---- Default date range to previous calendar month ----
  useEffect(() => {
    const now = new Date();
    const firstOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastOfPrevMonth = new Date(firstOfThisMonth - 1);
    const firstOfPrevMonth = new Date(lastOfPrevMonth.getFullYear(), lastOfPrevMonth.getMonth(), 1);

    setStartDate(formatDate(firstOfPrevMonth));
    setEndDate(formatDate(lastOfPrevMonth));
  }, []);

  // ---- Load tenants ----
  useEffect(() => {
    apiClient.get('/monthly-reports/tenants')
      .then(res => setTenants(res.data.tenants || []))
      .catch(err => console.error('Failed to load tenants:', err));
  }, []);

  // ---- Load campaigns when tenant changes ----
  useEffect(() => {
    setCampaigns([]);
    setSelectedCampaign('');
    if (!selectedTenant) return;

    apiClient.get(`/monthly-reports/campaigns/${selectedTenant}`)
      .then(res => setCampaigns(res.data.campaigns || []))
      .catch(err => console.error('Failed to load campaigns:', err));
  }, [selectedTenant]);

  // ---- Generate report ----
  const handleGenerate = useCallback(async () => {
    if (!selectedTenant || !startDate || !endDate) {
      setError('Please select a tenant and date range.');
      return;
    }

    setError('');
    setStatus(STATUS.GENERATING);
    setReportData(null);
    setPdfBase64(null);
    setPreviewOpen(false);

    try {
      const res = await apiClient.post('/monthly-reports/generate', {
        tenantId: selectedTenant,
        campaignId: selectedCampaign || null,
        startDate: `${startDate}T00:00:00Z`,
        endDate: `${endDate}T23:59:59Z`
      });

      setReportData({
        narrative: res.data.narrative,
        metrics: res.data.metrics,
        tenantEmail: res.data.tenantEmail,
        tenantName: res.data.tenantName
      });
      setPdfBase64(res.data.pdfBase64);
      setPdfFilename(res.data.filename);
      setStatus(STATUS.READY);
    } catch (err) {
      console.error('Generate failed:', err);
      setError(err.response?.data?.error || err.message || 'Generation failed');
      setStatus(STATUS.IDLE);
    }
  }, [selectedTenant, selectedCampaign, startDate, endDate]);

  // ---- Send (to tenant or override email) ----
  const handleSend = useCallback(async (emailOverride) => {
    if (!reportData || !pdfBase64) return;

    const targetEmail = emailOverride || reportData.tenantEmail;
    if (!targetEmail) {
      setError('No recipient email available.');
      return;
    }

    setStatus(STATUS.SENDING);
    setError('');
    setSendDropdownOpen(false);

    try {
      const monthYear = formatMonthYear(startDate, endDate);
      await apiClient.post('/monthly-reports/send', {
        tenantEmail: targetEmail,
        tenantName: reportData.tenantName,
        filename: pdfFilename,
        pdfBase64,
        monthYear,
        aiRepName: reportData.metrics?.aiRepName
      });
      setStatus(STATUS.SENT);
    } catch (err) {
      console.error('Send failed:', err);
      setError(err.response?.data?.error || err.message || 'Send failed');
      setStatus(STATUS.READY);
    }
  }, [reportData, pdfBase64, pdfFilename, startDate, endDate]);

  // ---- Download PDF ----
  const handleDownload = useCallback(() => {
    if (!pdfBase64) return;
    const link = document.createElement('a');
    link.href = `data:application/pdf;base64,${pdfBase64}`;
    link.download = pdfFilename || 'SurFox_Report.pdf';
    link.click();
  }, [pdfBase64, pdfFilename]);

  // ---- Discard ----
  const handleDiscard = useCallback(() => {
    setReportData(null);
    setPdfBase64(null);
    setPdfFilename('');
    setPreviewOpen(false);
    setSendDropdownOpen(false);
    setOverrideEmail('');
    setStatus(STATUS.DISCARDED);
    setTimeout(() => setStatus(STATUS.IDLE), 1500);
  }, []);

  // ---- Reset ----
  const handleReset = useCallback(() => {
    setReportData(null);
    setPdfBase64(null);
    setPdfFilename('');
    setError('');
    setPreviewOpen(false);
    setSendDropdownOpen(false);
    setOverrideEmail('');
    setStatus(STATUS.IDLE);
  }, []);

  // ---- Derived ----
  const recipientEmail = reportData?.tenantEmail
    || tenants.find(t => t.id === selectedTenant)?.notification_email
    || 'Not set';

  const tenantName = reportData?.tenantName
    || tenants.find(t => t.id === selectedTenant)?.name
    || '';

  const m = reportData?.metrics;

  return (
    <div className="px-6 py-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <FileText className="w-7 h-7 text-indigo-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Monthly Reports</h1>
          <p className="text-sm text-gray-500">Generate, preview, and send performance reports to tenants</p>
        </div>
      </div>

      {/* ---- Generation Form (always visible unless generating/sending) ---- */}
      {(status === STATUS.IDLE || status === STATUS.READY || status === STATUS.APPROVED || status === STATUS.DISCARDED) && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm mb-5">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Generate Report</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Tenant */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Tenant</label>
              <div className="relative">
                <select
                  value={selectedTenant}
                  onChange={e => setSelectedTenant(e.target.value)}
                  className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 appearance-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Select...</option>
                  {tenants.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Campaign */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Campaign (optional)</label>
              <div className="relative">
                <select
                  value={selectedCampaign}
                  onChange={e => setSelectedCampaign(e.target.value)}
                  disabled={!selectedTenant}
                  className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 appearance-none focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-50 disabled:text-gray-400"
                >
                  <option value="">All campaigns</option>
                  {campaigns.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {/* End Date */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 text-red-700 text-sm bg-red-50 border border-red-200 rounded-lg px-4 py-2.5 mt-4">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <button
            onClick={handleGenerate}
            disabled={!selectedTenant || !startDate || !endDate || status === STATUS.READY || status === STATUS.APPROVED}
            className="mt-4 flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-200 disabled:text-gray-400 text-white font-medium rounded-lg px-5 py-2.5 text-sm transition-colors"
          >
            <FileText className="w-4 h-4" />
            Generate Report
          </button>
        </div>
      )}

      {/* ---- Generating Spinner ---- */}
      {status === STATUS.GENERATING && (
        <div className="bg-white border border-gray-200 rounded-xl p-12 text-center space-y-4 shadow-sm">
          <Loader2 className="w-10 h-10 text-indigo-600 animate-spin mx-auto" />
          <div>
            <p className="text-lg font-semibold text-gray-800">Generating Report</p>
            <p className="text-sm text-gray-500 mt-1">Querying data, generating narrative, and rendering PDF...</p>
            <p className="text-xs text-gray-400 mt-3">This may take 30-60 seconds</p>
          </div>
        </div>
      )}

      {/* ---- Report Row: READY (review before approving) ---- */}
      {status === STATUS.READY && pdfBase64 && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="flex items-center gap-4 px-5 py-4">
            <div className="flex-shrink-0 w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-indigo-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">{pdfFilename}</p>
              <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
                <span>{tenantName}</span>
                {m && <span>{m.totalLeads} leads</span>}
                {m && <span>{m.hotCount} hot</span>}
                {m && <span>{m.replyRate}% reply rate</span>}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => setPreviewOpen(p => !p)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <Eye className="w-3.5 h-3.5" />
                {previewOpen ? 'Hide' : 'Preview'}
              </button>
              <button
                onClick={handleDownload}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <Download className="w-3.5 h-3.5" />
                Download
              </button>
              <button
                onClick={() => setStatus(STATUS.APPROVED)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors"
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                Approve
              </button>
              <button
                onClick={handleDiscard}
                title="Discard"
                className="flex items-center justify-center w-8 h-8 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
          {previewOpen && (
            <div className="border-t border-gray-200">
              <iframe
                src={`data:application/pdf;base64,${pdfBase64}`}
                title="Report Preview"
                className="w-full bg-white"
                style={{ height: '75vh' }}
              />
            </div>
          )}
        </div>
      )}

      {/* ---- Report Row: APPROVED (send options unlocked) ---- */}
      {status === STATUS.APPROVED && pdfBase64 && (
        <div className="bg-white border border-emerald-200 rounded-xl shadow-sm overflow-visible">
          <div className="flex items-center gap-4 px-5 py-4">
            <div className="flex-shrink-0 w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">{pdfFilename}</p>
              <p className="text-xs text-emerald-600 mt-0.5">Approved and ready to send</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => setPreviewOpen(p => !p)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <Eye className="w-3.5 h-3.5" />
                {previewOpen ? 'Hide' : 'Preview'}
              </button>
              <button
                onClick={handleDownload}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <Download className="w-3.5 h-3.5" />
                Download
              </button>
              <div className="relative">
                <button
                  onClick={() => setSendDropdownOpen(o => !o)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors"
                >
                  <Send className="w-3.5 h-3.5" />
                  Send
                  <ChevronDown className="w-3 h-3" />
                </button>
                {sendDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-xl shadow-lg z-20 p-4 space-y-3">
                    <button
                      onClick={() => handleSend()}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-gray-800 hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      <Send className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="font-medium">Send to tenant</p>
                        <p className="text-xs text-gray-500 truncate">{recipientEmail}</p>
                      </div>
                    </button>
                    <div className="border-t border-gray-100 pt-3">
                      <label className="block text-xs font-medium text-gray-500 mb-1.5">Or send to a different email</label>
                      <div className="flex gap-2">
                        <input
                          type="email"
                          value={overrideEmail}
                          onChange={e => setOverrideEmail(e.target.value)}
                          placeholder="you@example.com"
                          className="flex-1 bg-white border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          onKeyDown={e => { if (e.key === 'Enter' && overrideEmail) handleSend(overrideEmail); }}
                        />
                        <button
                          onClick={() => overrideEmail && handleSend(overrideEmail)}
                          disabled={!overrideEmail}
                          className="px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-200 disabled:text-gray-400 rounded-lg transition-colors"
                        >
                          Send
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <button
                onClick={handleDiscard}
                title="Discard"
                className="flex items-center justify-center w-8 h-8 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
          {previewOpen && (
            <div className="border-t border-gray-200">
              <iframe
                src={`data:application/pdf;base64,${pdfBase64}`}
                title="Report Preview"
                className="w-full bg-white"
                style={{ height: '75vh' }}
              />
            </div>
          )}
        </div>
      )}

      {/* ---- Sending State ---- */}
      {status === STATUS.SENDING && (
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center space-y-3 shadow-sm">
          <Loader2 className="w-8 h-8 text-emerald-600 animate-spin mx-auto" />
          <p className="text-sm font-medium text-gray-700">Sending to {overrideEmail || recipientEmail}...</p>
        </div>
      )}

      {/* ---- Sent Success ---- */}
      {status === STATUS.SENT && (
        <div className="bg-white border border-emerald-200 rounded-xl px-5 py-4 shadow-sm flex items-center gap-4">
          <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-800">Report sent</p>
            <p className="text-xs text-gray-500">
              Delivered to {overrideEmail || reportData?.tenantEmail}
            </p>
          </div>
          <button
            onClick={handleReset}
            className="text-sm text-indigo-600 hover:text-indigo-700 font-medium transition-colors"
          >
            Generate another
          </button>
        </div>
      )}

      {/* Close dropdown on outside click */}
      {sendDropdownOpen && (
        <div className="fixed inset-0 z-10" onClick={() => setSendDropdownOpen(false)} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function formatDate(d) {
  return d.toISOString().split('T')[0];
}

function formatMonthYear(startStr, endStr) {
  const start = new Date(startStr);
  const end = new Date(endStr);
  const months = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
    return `${months[start.getMonth()]} ${start.getFullYear()}`;
  }
  return `${months[start.getMonth()]} ${start.getDate()} - ${months[end.getMonth()]} ${end.getDate()}, ${end.getFullYear()}`;
}
